import { computed, toValue } from 'vue';

// Régua ÚNICA de tier do lead, compartilhada por todos os badges/painéis
// de score (LeadScoreBadge, LeadScorePanel, VoipScoreBadge). O perfil
// salvo pela IA (lead_profile) tem prioridade sobre o score numérico.
export const LEAD_TIER_THRESHOLDS = { HOT: 80, WARM: 50 };

const TIER_META = {
  hot: {
    tier: 'hot',
    label: 'Quente',
    colorClass:
      'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800',
    barClass: 'bg-red-500',
    icon: 'i-ri-fire-line',
  },
  warm: {
    tier: 'warm',
    label: 'Morno',
    colorClass:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    barClass: 'bg-amber-500',
    icon: 'i-ri-sun-line',
  },
  cold: {
    tier: 'cold',
    label: 'Frio',
    colorClass:
      'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-800',
    barClass: 'bg-sky-500',
    icon: 'i-ri-snowflake-line',
  },
};

/**
 * Versão pura da régua — útil fora de contexto reativo (ex.: barras de
 * critério no popover). Retorna null quando não há perfil nem score válido.
 */
export function resolveLeadTier(score, profile = null) {
  const p = (profile || '').toString().toLowerCase();
  if (TIER_META[p]) return TIER_META[p];

  // Number(null) e Number('') viram 0 — tratamos como "sem score"
  if (score === null || score === undefined || score === '') return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;

  if (n >= LEAD_TIER_THRESHOLDS.HOT) return TIER_META.hot;
  if (n >= LEAD_TIER_THRESHOLDS.WARM) return TIER_META.warm;
  return TIER_META.cold;
}

/**
 * Composable reativo da régua de tier do lead.
 *
 * @param {import('vue').MaybeRefOrGetter<number|string|null>} score
 * @param {import('vue').MaybeRefOrGetter<string|null>} profile lead_profile da IA ('hot'|'warm'|'cold', case insensitive)
 * @returns {{ tier, label, colorClass, barClass, icon }} computeds (null/'' quando sem dados)
 */
export function useLeadTier(score, profile = null) {
  const meta = computed(() => resolveLeadTier(toValue(score), toValue(profile)));

  return {
    tier: computed(() => meta.value?.tier ?? null),
    label: computed(() => meta.value?.label ?? ''),
    colorClass: computed(() => meta.value?.colorClass ?? ''),
    barClass: computed(() => meta.value?.barClass ?? ''),
    icon: computed(() => meta.value?.icon ?? ''),
  };
}

export default useLeadTier;
